// api
import type {
  EditUnitDto,
  ReadUnitDto,
  UnitsPatchAsyncByidunitApiArg,
} from '@/api/generated/recalc/endpoints/v1/units';

// shared components
import { EMPTY_ADDRESS_VALUE } from '@/components/ui/form';

// relative
import type { EditUnitFormValues } from './editUnitForm.schema';
import type {
  SelectedUnitType,
  UnitInputMode,
  UnitTypeInputFormValue,
} from './unitTypeInput.schema';

export function buildUnitTypeInputsFromUnit(
  unitType: SelectedUnitType,
  unit: ReadUnitDto,
): Record<string, UnitTypeInputFormValue> {
  const unitTypeInputs: Record<string, UnitTypeInputFormValue> = {};

  (unitType.UnitTypeInputs ?? []).forEach((input) => {
    const existingSensor = unit.UnitSensors?.find(
      (unitSensor) =>
        unitSensor.UnitTypeInput?.UnitTypeInputId === input.UnitTypeInputId,
    );

    if (!existingSensor) {
      unitTypeInputs[input.UnitTypeInputId] = {
        inputType: 'sensorId',
        sensorId: '',
        externalSensorId: '',
        apiInput: false,
        customName: '',
        channelNumber: null,
      };
      return;
    }

    let inputType: UnitInputMode = 'sensorId';
    if (existingSensor.ApiInput) {
      inputType = 'apiInput';
    } else if (existingSensor.ExternalSensor) {
      inputType = 'externalSensorId';
    }

    unitTypeInputs[input.UnitTypeInputId] = {
      inputType,
      sensorId: existingSensor.Sensor?.SensorId?.toString() ?? '',
      externalSensorId:
        existingSensor.ExternalSensor?.ExternalSensorId?.toString() ?? '',
      apiInput: existingSensor.ApiInput ?? false,
      customName: existingSensor.CustomName ?? '',
      channelNumber: existingSensor.ChannelNumber ?? null,
    };
  });

  return unitTypeInputs;
}

export function toEditUnitFormValues(
  unit: ReadUnitDto,
  unitType: SelectedUnitType,
): EditUnitFormValues {
  return {
    name: unit.Name ?? '',
    description: unit.Description ?? '',
    address: {
      ...EMPTY_ADDRESS_VALUE,
      latitude: unit.Latitude ?? null,
      longitude: unit.Longitude ?? null,
    },
    tags: unit.Tags?.map((tag) => tag.Text) ?? [],
    unitTypeInputs: buildUnitTypeInputsFromUnit(unitType, unit),
  };
}

export function toPatchUnitPayload(
  values: EditUnitFormValues,
  unit: ReadUnitDto,
): UnitsPatchAsyncByidunitApiArg {
  const hasCoords =
    values.address.latitude != null && values.address.longitude != null;

  // The generated `EditUnitDto` types Latitude/Longitude as plain optional
  // numbers (no `| null`), but the PATCH endpoint treats an explicit `null`
  // as "clear this field", matching the legacy unitsApiSlice.jsx behavior
  // this replaces - same cast-once pattern as editGatewayForm.mappers.ts.
  const editUnitDto = {
    Name: values.name.trim(),
    Description: values.description.trim() || null,
    Latitude: hasCoords ? values.address.latitude : null,
    Longitude: hasCoords ? values.address.longitude : null,
    // Not user-editable on this form - pass the unit's existing value through
    // unchanged.
    GpsAccuracy: unit.GpsAccuracy ?? 0,
    UnitSensors: Object.entries(values.unitTypeInputs)
      .filter(
        ([, inputData]) =>
          (inputData.inputType === 'sensorId' && inputData.sensorId) ||
          (inputData.inputType === 'externalSensorId' &&
            inputData.externalSensorId) ||
          inputData.inputType === 'apiInput',
      )
      .map(([inputId, inputData]) => ({
        UnitTypeInput: { UnitTypeInputId: parseInt(inputId, 10) },
        Sensor:
          inputData.inputType === 'sensorId'
            ? { SensorId: parseInt(inputData.sensorId, 10) }
            : null,
        ExternalSensor:
          inputData.inputType === 'externalSensorId'
            ? { ExternalSensorId: parseInt(inputData.externalSensorId, 10) }
            : null,
        ApiInput: inputData.inputType === 'apiInput',
        ChannelNumber: inputData.channelNumber ?? 1,
        CustomName: inputData.customName?.trim() || null,
      })),
  } as EditUnitDto;

  return { id: unit.UnitId, editUnitDto };
}
